import { useState, ButtonHTMLAttributes } from 'react';
import NavButton from './Navigation/NavButton.tsx';
import { Superscript } from './Icons.tsx';
import Popover from './Popover.tsx';
import ReferenceImage from '@/assets/reference.png';
import ReferenceImage2x from '@/assets/reference@2x.png';
import { TypoFootnote } from '@/components/Typography.tsx';

const ReferenceButton = (props: ButtonHTMLAttributes<HTMLButtonElement>) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>();

  return (
    <>
      <NavButton {...props} ref={setAnchor} Icon={Superscript} onClick={() => setOpen(x => !x)}>reference</NavButton>
      <ReferenceDialog
        open={open}
        anchor={anchor}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

interface DialogProps {
  open: boolean;
  onClose: () => void;
  anchor?: Element | null;
}

const ReferenceDialog = ({ open, anchor, onClose }: DialogProps) => {
  // Mobile: the sheet is ~390px wide and the sheet image is 45rem, so pinch-zoom
  // alone is not enough to read it.
  const [zoomed, setZoomed] = useState(false);

  return (
    <Popover
      open={open}
      title="Reference Sheet"
      anchor={anchor}
      placement="auto"
      onClose={onClose}
      width="45rem"
      full
    >
      <div className="hidden m:flex m:items-center m:justify-end m:gap-2 m:pb-2">
        <ZoomButton active={!zoomed} onClick={() => setZoomed(false)}>Fit</ZoomButton>
        <ZoomButton active={zoomed} onClick={() => setZoomed(true)}>200%</ZoomButton>
      </div>

      <img
        src={ReferenceImage}
        srcSet={`${ReferenceImage} 1x, ${ReferenceImage2x} 2x`}
        alt="Math Reference"
        className={`m:w-full m:[touch-action:pinch-zoom]
                    ${zoomed ? 'm:max-w-none m:w-[200%]' : ''}`}
      />

      <div className="flex flex-col gap-1 text-neutral-text mt-2">
        <TypoFootnote>The number of degrees of arc in a circle is 360.</TypoFootnote>
        <TypoFootnote>The number of radians of arc in a circle is 2&pi;.</TypoFootnote>
        <TypoFootnote>The sum of the measures in degrees of the angles of a triangle is 180.</TypoFootnote>
      </div>
    </Popover>
  );
};

const ZoomButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) => (
  <button
    onClick={onClick}
    className={`h-11 min-w-11 rounded-xs px-3 text-callout font-medium active:bg-shade-10
                ${active ? 'bg-neutral2-canvas text-neutral2-canvas-text' : 'text-neutral2-text'}`}
  >
    {children}
  </button>
);

export default ReferenceButton;
